import { ModeEdit, Save } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Button, Popover, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { bookingsAPI } from 'api/bookingsAPI'
import handleCancelBooking from 'components/Bookings/Common/CancelBooking'
import notifyPopup from 'components/Common/Notification/notifyPopup'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { copyBooking } from 'reducers/bookingsReducer'

type HeaderProps = {
  currentBooking: any
  setOpenViewEditModal: (e: any) => void
  editMode: boolean
  editStates: any
  setEditMode: (e: boolean) => void
  preparedAmend: any
  submitAmend: any
}

type DetailProps = {
  text: string
  showBorder?: boolean
}

const Detail = ({ text, showBorder = false }: DetailProps) => {
  return (
    <Typography
      component="p"
      sx={(theme) => ({
        color: theme.palette.common.white,
        borderRight: showBorder ? 3 : 0,
        borderColor: theme.palette.primary.main,
        padding: '0px',
      })}
    >
      {text}
    </Typography>
  )
}

const Header = ({
  currentBooking,
  preparedAmend,
  setEditMode,
  editMode,
  editStates,
  setOpenViewEditModal,
  submitAmend,
}: HeaderProps) => {
  const cancelBooking = useMutation({
    mutationKey: ['cancelBooking', currentBooking?.booking.id],
    mutationFn: async () => {
      return await bookingsAPI.cancelBooking(currentBooking?.booking.id)
    },
    onMutate: () => {
      return { id: 1 }
    },
    onError: (error) => {
      notifyPopup({
        type: 'danger',
        title: 'Unable to cancel booking',
        message: `${error}: ${error}`,
      })
    },
    onSuccess: () => {
      notifyPopup({
        type: 'success',
        title: 'Booking Cancelled',
        message: 'Your request to cancel a booking has been processed.',
      })
      setOpenViewEditModal(false)
    },
  })

  const dispatch = useDispatch()
  const history = useHistory()

  useEffect(() => {
    console.log(preparedAmend)
  }, [preparedAmend])

  const sendEmail = async (template: string) => {
    try {
      bookingsAPI.resendCommunication(currentBooking?.booking.id, template)
    } catch (error) {
      console.log('error', error)
    }
  }
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const isAnyEditing = Object.values(editStates).some((v) => v === true)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  return (
    <>
      <Box
        component="div"
        sx={(theme) => ({
          width: '100%',
          backgroundColor: theme.palette.brand.main,
          color: theme.palette.common.white,
          display: 'flex',
          flexDirection: 'row',
          gap: '5px',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingX: '20px',
          paddingY: '5px',
        })}
      >
        <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              textTransform: 'uppercase',
            }}
          >
            <Detail
              text={`Booking ${currentBooking?.booking.bookingReference}`}
            />
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          <Button
            onClick={() => {
              dispatch(copyBooking(currentBooking.booking?.id))
              history.push('/admin/bookings/create/account?type=taxi')
            }}
          >
            Copy Booking
          </Button>
          <Button
            aria-describedby={id}
            variant="contained"
            onClick={handleClick}
          >
            Send Email
          </Button>

          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'column', paddingX: '5px' }}
            >
              <Button
                variant="text"
                onClick={() => sendEmail('Booking_Confirmation')}
                sx={{
                  backgroundColor: '#ffffff',
                  color: '#000',
                  justifyContent: 'flex-start',
                  boxShadow: 'none',
                  ':hover': { backgroundColor: '#aaaaaa22' },
                }}
              >
                Booking Confirmation
              </Button>
              {currentBooking.booking.amendedSinceBooking ? (
                <Button
                  variant="text"
                  onClick={() => sendEmail('Booking_Amended')}
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#000',
                    justifyContent: 'flex-start',
                    boxShadow: 'none',
                    ':hover': { backgroundColor: '#aaaaaa22' },
                  }}
                >
                  Amendment Confirmation
                </Button>
              ) : (
                <></>
              )}
              {currentBooking.booking.bookingStatusTypeName === 'Cancelled' ? (
                <Button
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#000',
                    justifyContent: 'flex-start',
                    boxShadow: 'none',
                    ':hover': { backgroundColor: '#aaaaaa22' },
                  }}
                  onClick={() => sendEmail('Booking_Cancelled')}
                >
                  Cancellation Confirmation
                </Button>
              ) : (
                <></>
              )}
            </Box>
          </Popover>
          <Button
            color="error"
            disabled={!currentBooking.jobs[0].policy.isCancellable}
            onClick={() =>
              handleCancelBooking({
                action: cancelBooking.mutate,
                bookingReference: currentBooking.booking.bookingReference,
                bookingJobs: currentBooking.jobs,
                booking: currentBooking.booking,
              })
            }
          >
            Cancel Booking
          </Button>
          {editMode || !currentBooking.policy.anyAmendable ? (
            <LoadingButton
              color="success"
              loading={submitAmend.isLoading}
              loadingPosition="end"
              disabled={
                typeof preparedAmend === 'undefined' ||
                submitAmend.isLoading ||
                isAnyEditing
              }
              onClick={() => {
                submitAmend.mutate(preparedAmend)
              }}
              endIcon={<Save />}
              sx={{
                backgroundColor: 'green',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'darkgreen',
                },
              }}
            >
              Update Booking
            </LoadingButton>
          ) : (
            <Button
              onClick={() => setEditMode(!editMode)}
              endIcon={<ModeEdit />}
              sx={{
                backgroundColor: 'green',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'darkgreen',
                },
              }}
            >
              Edit Booking
            </Button>
          )}
        </Box>
      </Box>
    </>
  )
}

export default Header
